import type {Component, Editor} from 'grapesjs';
import {MULTI_MEDIA_SOURCE_GETTY} from '@Libraries/user-media-library';
import {openMessageModal} from '@Components/modals/message-modal';
import {ToastType} from '@Components/toast/toast.types';
import {EMAIL_MAKER_COMPONENT_TYPES, EMAIL_MAKER_PROPS, MAX_VIDEOS_ALLOWED} from '@EmailMakerReact/email-maker-react.constants';
import {EmailMakerMediaType} from '@EmailMakerReact/email-maker-react.types';
import {getBooleanProp, getStringProp} from '@EmailMakerReact/lib/email-maker-component-query-library';
import {updateTextComponentLinkColor} from '@EmailMakerReact/lib/email-maker-component-style-library';
import {canAddUniqueGettyAsset, isEmailContentMounted, showGettyLimitMessage, syncGettyCountForComponents} from '@EmailMakerReact/lib/email-maker-getty-library';
import {assignFreshCountdownId, isCountdownComponent} from '@EmailMakerReact/lib/email-maker-countdown-library';
import {getVideoCount} from '@EmailMakerReact/lib/email-maker-media-library';
import {moveComponentAnchor} from '@EmailMakerReact/lib/email-maker-anchor-link-library';
import {onAllComponentsLoaded} from '@EmailMakerReact/lib/email-maker-misc-library';
import {sanitizeComponentContent} from '@EmailMakerReact/lib/email-maker-sanitize-library';

/** GrapesJS core's log message when a drop target rejects a component — matched by string. */
const INVALID_TARGET_ERROR = 'Invalid target position';
const WARNING_TOAST_DURATION_MS = 3000;

interface ComponentRemoveOptions {
  abort?: number;
}

interface ComponentDragEndData {
  target: Component;
}

/** Minimal shape of the gjs core select-comp command this file extends. */
interface SelectComponentCommandContext {
  canvas: {
    getCanvasView(): {
      getElementPos(el: HTMLElement): unknown;
    };
  };
}

export function initComponentListeners(editor: Editor): () => void {
  const onComponentCreate = (component: Component): void => {
    syncGettyCountForComponent(editor, component);
  };

  const onComponentRemoveBefore = (component: Component, _removeFunc: unknown, opts: ComponentRemoveOptions): void => {
    if (typeof component.get('deletable') !== 'undefined' && component.get('deletable') === false) {
      opts.abort = 1;
    }
  };

  const onComponentMount = (component: Component): void => {
    if (component.is(EMAIL_MAKER_COMPONENT_TYPES.container.PMW_CONTENT)) {
      onAllComponentsLoaded(editor);
    }

    if (isEmailContentMounted(editor) && !canPremiumContentBeAdded(editor, component)) {
      const mediaType = component.get('type') === EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_IMAGE ? EmailMakerMediaType.IMAGE : EmailMakerMediaType.VIDEO;
      showGettyLimitMessage(mediaType);
      editor.getModel().skip(() => component.remove());
    } else if (!canVideoBeAdded(editor, component)) {
      editor.getModel().skip(() => component.remove());
      openMessageModal({
        title: window.i18next.t('pmwjs_video_limit'),
        text: window.i18next.t('pmwjs_video_limit_message', {num: MAX_VIDEOS_ALLOWED}),
      });
    }
  };

  const onComponentAdd = (component: Component): void => {
    updateTextComponentLinkColor(editor, component);
  };

  const onComponentClone = (component: Component): void => {
    if (isCountdownComponent(component)) {
      assignFreshCountdownId(component);
    }
  };

  const onComponentUpdate = (component: Component): void => {
    sanitizeComponentContent(component);
    updateTextComponentLinkColor(editor);
  };

  const onComponentRemove = (component: Component): void => {
    syncGettyCountForComponent(editor, component);
  };

  const onComponentDragEnd = ({target}: ComponentDragEndData): void => {
    moveComponentAnchor(editor, target);
  };

  const onLog = (msg: string): void => {
    if (msg === INVALID_TARGET_ERROR) {
      window.PMW.showToast({duration: WARNING_TOAST_DURATION_MS, title: window.i18next.t('pmwjs_invalid_target_error'), type: ToastType.WARNING});
    }
  };

  extendSelectComponentCommand(editor);

  editor.on('component:create', onComponentCreate);
  editor.on('component:remove:before', onComponentRemoveBefore);
  editor.on('component:mount', onComponentMount);
  editor.on('component:add', onComponentAdd);
  editor.on('component:clone', onComponentClone);
  editor.on('component:update', onComponentUpdate);
  editor.on('component:remove', onComponentRemove);
  editor.on('component:drag:end', onComponentDragEnd);
  editor.on('log', onLog);

  return (): void => {
    editor.off('component:create', onComponentCreate);
    editor.off('component:remove:before', onComponentRemoveBefore);
    editor.off('component:mount', onComponentMount);
    editor.off('component:add', onComponentAdd);
    editor.off('component:clone', onComponentClone);
    editor.off('component:update', onComponentUpdate);
    editor.off('component:remove', onComponentRemove);
    editor.off('component:drag:end', onComponentDragEnd);
    editor.off('log', onLog);
  };
}

/** Extends the gjs core select-comp command so element positions are computed from the canvas view. Editor-scoped, so it dies with the editor (no teardown). */
function extendSelectComponentCommand(editor: Editor): void {
  editor.Commands.extend('select-comp', {
    getElementPos(this: SelectComponentCommandContext, el: HTMLElement): unknown {
      return this.canvas.getCanvasView().getElementPos(el);
    },
  });
}

function syncGettyCountForComponent(editor: Editor, component: Component): void {
  const style = component.get('style') as Record<string, unknown> | undefined;
  const hasBg = style?.['background-image'];
  const hasImg = getBooleanProp(component, EMAIL_MAKER_PROPS.IS_MEDIA);

  if (!(hasImg || hasBg) || component.get(EMAIL_MAKER_PROPS.CONTENT_SOURCE) !== MULTI_MEDIA_SOURCE_GETTY) {
    return;
  }

  syncGettyCountForComponents(editor);
}

function canPremiumContentBeAdded(editor: Editor, component: Component): boolean {
  if (component.get(EMAIL_MAKER_PROPS.CONTENT_SOURCE) !== MULTI_MEDIA_SOURCE_GETTY) {
    return true;
  }

  const hasBg = Boolean(component.getStyle()['background-image']);
  const hasSrc = Boolean(component.get('src'));
  const isImg = hasSrc && component.get('type') === EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_IMAGE;
  const isVideo = hasSrc && component.get('type') === EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_VIDEO;

  if (isVideo) {
    return canAddGettyComponent(editor, component, EmailMakerMediaType.VIDEO);
  }

  if (hasBg || isImg) {
    return canAddGettyComponent(editor, component, EmailMakerMediaType.IMAGE);
  }

  return true;
}

function canAddGettyComponent(editor: Editor, component: Component, mediaType: EmailMakerMediaType): boolean {
  return canAddUniqueGettyAsset(editor, {
    mediaType,
    candidateHashedFilename: getStringProp(component, EMAIL_MAKER_PROPS.HASHED_FILENAME) ?? '',
    excludeComponent: component,
  });
}

function canVideoBeAdded(editor: Editor, component: Component): boolean {
  const bg = component.getStyle()['background-image'];
  const hasBgVideo = bg && typeof bg === 'string' && /\.gif/gi.test(bg);
  const isVideo = component.is(EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_VIDEO);

  if (!hasBgVideo && !isVideo) {
    return true;
  }

  const currentVideoCount = getVideoCount(editor);
  const allowedCount = MAX_VIDEOS_ALLOWED - currentVideoCount;
  return allowedCount >= 0;
}
